import { createTheme } from "@mui/material/styles";
import { nlNL as coreNlNL } from "@mui/material/locale";
import { nlNL as dataGridNlNL } from "@mui/x-data-grid/locales";

// Central application theme to consolidate styling concerns.
export const createAppTheme = () => {
  const base = createTheme({
    palette: {
      primary: { main: "#eb108b", contrastText: "#ffffff" },
      secondary: { main: "#eb108b", contrastText: "#ffffff" },
      error: { main: "#ec2027", contrastText: "#ffffff" },
      background: { default: "#f7f7f9", paper: "#ffffff" },
    },
    shape: { borderRadius: 6 },
    spacing: 8,
    typography: {
      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
      button: { textTransform: "none", fontWeight: 600 },
    },
  });

  // Merge Dutch locale for both Material UI and DataGrid so built-in strings
  // (including table filter labels, column menu and toolbar) are localized.
  return createTheme(base, coreNlNL, dataGridNlNL, {
    components: {
      MuiCssBaseline: {
        styleOverrides: `body { background-image: url("/media/img/shb-patroon-grijs.png"); }`,
      },
      MuiPaper: {
        defaultProps: { elevation: 1 },
        styleOverrides: {
          root: {
            marginTop: base.spacing(2.5), // ~20px
            padding: 0,
          },
        },
        variants: [
          {
            props: { variant: "section" },
            style: {
              padding: base.spacing(3),
              marginTop: base.spacing(3),
            },
          },
        ],
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: base.shape.borderRadius, fontWeight: 600 },
          containedPrimary: { boxShadow: "none" },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: { marginTop: base.spacing(2) },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: ({ theme }) => ({
            "&.MuiInputLabel-shrink": {
              backgroundColor: theme.palette.background.paper,
              padding: "0 4px",
              lineHeight: 1.2,
              zIndex: 1,
              transformOrigin: "top left",
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            transition: "background-color 120ms ease",
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 2,
            },
          },
          notchedOutline: {
            transition:
              "border-color 120ms ease-out, box-shadow 120ms ease-out",
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: { marginTop: base.spacing(2) },
        },
      },
      MuiSelect: {
        styleOverrides: {
          outlined: { backgroundColor: "#fff" },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            borderRadius: base.shape.borderRadius,
            backgroundColor: base.palette.background.paper,
            // Force rows & cells to use white background (remove grey stripe/inherited bg)
            "& .MuiDataGrid-row, & .MuiDataGrid-cell": {
              backgroundColor: base.palette.background.paper,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: base.palette.action.hover,
            },
            // Ensure internal scroll area also white
            "& .MuiDataGrid-virtualScroller, & .MuiDataGrid-virtualScrollerContent":
              {
                backgroundColor: base.palette.background.paper,
              },
          },
          columnHeaders: { background: "#fafafa" },
        },
      },
    },
  });
};
